import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography
} from "@material-ui/core";
import CloseIconModal from "assets/icons/CloseIconModal";
import CustomRoundRadio from "components/CustomRoundRadio";
import CustomSquareCheckbox from "components/CustomSquareCheckbox";
import { CustomTextArea } from "components/CustomTextArea";
import { useEffect, useState } from "react";
import { postRespostaMultiplosPerfis } from "services/pesquisa";
import snackbarUtilsInstance from "utils/SnackbarUtils";
import { z } from 'zod';
import "./index.scss";

interface ModalExperienciaFormularioProps {
  open: boolean;
  onSuccess: () => void;
  onClose: () => void;
  languageValues: any;
}

const ModalExperienciaFormulario: React.FC<ModalExperienciaFormularioProps> = ({
  open,
  onSuccess,
  onClose,
  languageValues
}) => {

  const experienciaSchema = z.object({
    checkBoxUm: z.array(z.string()).min(1, "É necessário selecionar ao menos um dos perfis relacionados à sua atividade atual na plataforma."),
    valueRadialDois: z.number({ message: "É necessário selecionar uma das opções referente à região em que atua." }),
    valueRadialTres: z.number({ message: "É necessário selecionar uma das opções referente à dificuldade de acesso da plataforma." }),
    inputFieldQuatro: z.string().max(1500)
  });

  type ExperienciaForm = z.infer<typeof experienciaSchema>;

  const [loading, setLoading] = useState<boolean>(false);
  const [checkBoxUm, setCheckBoxUm] = useState<Array<string>>([]);
  const [valueRadialDois, setValueRadialDois] = useState<number | null>(null);
  const [valueRadialTres, setValueRadialTres] = useState<number | null>(null);
  const [inputFieldQuatro, setInputFieldQuatro] = useState<string>("");
  const [formValidation, setFormValidation] = useState<z.SafeParseReturnType<ExperienciaForm, ExperienciaForm>>();

  useEffect(() => {
    const result = experienciaSchema.safeParse({
      checkBoxUm,
      valueRadialDois,
      valueRadialTres,
      inputFieldQuatro
    });

    setFormValidation(result);
    // eslint-disable-next-line
  }, [checkBoxUm, valueRadialDois, valueRadialTres, inputFieldQuatro]);

  const handleSubmit = () => {
    setLoading(true);
    if (!formValidation?.success) {
      snackbarUtilsInstance.error(languageValues.modalExperiencia.errors.formularioInvalido, {style: {zIndex: 99999}});
      setLoading(false);
      return;
    }

    const formData = formValidation.data;
    postRespostaMultiplosPerfis({
      primeiraPergunta: formData.checkBoxUm,
      segundaPergunta: formData.valueRadialDois,
      terceiraPergunta: formData.valueRadialTres,
      quartaPergunta: formData.inputFieldQuatro
    }).then(() => {
      onSuccess();
    }).catch(() => {
      snackbarUtilsInstance.error(languageValues.modalExperiencia.errors.falhaAoEnviar)
    }).finally(() => {
      setLoading(false);
    });
  };

  const handleCheckItem = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.checked) {
      setCheckBoxUm([...checkBoxUm, e.target.id]);
      return;
    }
    setCheckBoxUm(checkBoxUm.filter(item => item !== e.target.id));
  }

  const isBoxChecked = (id: string): boolean => {
    return checkBoxUm.includes(id);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      closeAfterTransition
      disableBackdropClick
      disableEscapeKeyDown
      maxWidth="md"
      PaperProps={{
        className: 'ModalExperiencia_paper'
      }}
      style={{
        zIndex: 9999
      }}
    >
      <Box>
        <DialogTitle className='ModalExperiencia_dialogTitle'>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography className='ModalExperiencia_titleModal'>
              {languageValues.modalAvisoGenericTitle}
            </Typography>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              size="small"
            >
              <CloseIconModal style={{ width: 'auto', height: 25 }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent style={{ marginTop: 20 }}>
          <Grid container spacing={2} justifyContent="center" alignItems="flex-start">
            <Grid item xs={12} md={10}>
              <div className="ModalExperiencia_formTitle">{languageValues.modalExperiencia.formulario.title}</div>
            </Grid>

            <Grid item xs={12} md={10}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ marginRight: 8 }} className="ModalExperiencia_questionTitle">1.</div>
                <div>
                  <div className="ModalExperiencia_questionTitle">
                    {languageValues.modalExperiencia.formulario.question1}
                  </div>
                  <div className="ModalExperiencia_questionSubtitle">
                    {languageValues.modalExperiencia.formulario.question1Sub}
                  </div>
                </div>
              </div>
              <Grid item xs={12} md={10} style={{ marginLeft: 14 }}>
                <Grid container spacing={1} style={{ marginTop: 4 }}>
                  <Grid item xs={12} md={10}>
                    <CustomSquareCheckbox
                      id={"pufv_professor"}
                      checked={isBoxChecked("pufv_professor")}
                      onChange={handleCheckItem}
                      label={languageValues.modalExperiencia.formulario.labels.question1.check1}
                    />
                  </Grid>
                  <Grid item xs={12} md={10} style={{ marginTop: 0 }}>
                    <CustomSquareCheckbox
                      id={"pufv_professor_gestor"}
                      checked={isBoxChecked("pufv_professor_gestor")}
                      onChange={handleCheckItem}
                      label={languageValues.modalExperiencia.formulario.labels.question1.check2}
                    />
                  </Grid>
                  <Grid item xs={12} md={10} style={{ marginTop: 0 }}>
                    <CustomSquareCheckbox
                      id={"pufv_coordenador_local"}
                      checked={isBoxChecked("pufv_coordenador_local")}
                      onChange={handleCheckItem}
                      label={languageValues.modalExperiencia.formulario.labels.question1.check3}
                    />
                  </Grid>
                  <Grid item xs={12} md={10} style={{ marginTop: 0 }}>
                    <CustomSquareCheckbox
                      id={"pufv_assessor_pedagogico"}
                      checked={isBoxChecked("pufv_assessor_pedagogico")}
                      onChange={handleCheckItem}
                      label={languageValues.modalExperiencia.formulario.labels.question1.check4}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={10}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ marginRight: 8 }} className="ModalExperiencia_questionTitle">2.</div>
                <div className="ModalExperiencia_questionTitle">
                  {languageValues.modalExperiencia.formulario.question2}
                </div>
              </div>
              <Grid item xs={12} md={10} style={{ marginLeft: 10 }}>
                <Grid container spacing={2} style={{ marginTop: 4 }}>
                  <Grid item xs={2}>
                    <CustomRoundRadio
                      checked={valueRadialDois === 1}
                      onChange={(e) => setValueRadialDois(Number(e.target.value))}
                      value={1}
                      name="grupo1"
                      label={languageValues.modalExperiencia.formulario.labels.question2.radio1}
                      labelPosition="top"
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <CustomRoundRadio
                      checked={valueRadialDois === 0}
                      onChange={(e) => setValueRadialDois(Number(e.target.value))}
                      value={0}
                      name="grupo1"
                      label={languageValues.modalExperiencia.formulario.labels.question2.radio2}
                      labelPosition="top"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={10}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ marginRight: 8 }} className="ModalExperiencia_questionTitle">3.</div>
                <div className="ModalExperiencia_questionTitle">
                  {languageValues.modalExperiencia.formulario.question3}
                </div>
              </div>
              <Grid item xs={12} md={10} style={{ marginLeft: 10 }}>
                <Grid container spacing={2} style={{ marginTop: 4 }}>
                  <Grid item xs={2}>
                    <CustomRoundRadio
                      checked={valueRadialTres === 1}
                      onChange={(e) => setValueRadialTres(Number(e.target.value))}
                      value={1}
                      name="grupo2"
                      label={languageValues.modalExperiencia.formulario.labels.question3.radio1}
                      labelPosition="top"
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <CustomRoundRadio
                      checked={valueRadialTres === 0}
                      onChange={(e) => setValueRadialTres(Number(e.target.value))}
                      value={0}
                      name="grupo2"
                      label={languageValues.modalExperiencia.formulario.labels.question3.radio2}
                      labelPosition="top"
                    />
                  </Grid>
                  <Grid item xs={4} sm={3}>
                    <CustomRoundRadio
                      checked={valueRadialTres === 2}
                      onChange={(e) => setValueRadialTres(Number(e.target.value))}
                      value={2}
                      name="grupo2"
                      label={languageValues.modalExperiencia.formulario.labels.question3.radio3}
                      labelPosition="top"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={10}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ marginRight: 8 }} className="ModalExperiencia_questionTitle">4.</div>
                <div className="ModalExperiencia_questionTitle">
                  {languageValues.modalExperiencia.formulario.question4}
                </div>
              </div>
              <Grid item xs={12} style={{ marginTop: 15 }}>
                <CustomTextArea
                  id="ModalExperiencia_textFieldQuatro"
                  counter={true}
                  counterOrderAsc={true}
                  maxSize={1500}
                  value={inputFieldQuatro}
                  color={'primary'}
                  helperText={languageValues.modalExperiencia.formulario.labels.question4.helper}
                  placeholder={languageValues.modalExperiencia.formulario.labels.question4.placeholder}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputFieldQuatro(e.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>

        <Container className='ModalExperiencia_dialogActions'>
          <Box className="ModalExperiencia_container-box-btn">
            <div className="ModalExperiencia_btn">
              <Button
                className='ModalExperiencia_action-button'
                variant='contained'
                onClick={handleSubmit}
                color="inherit"
                disabled={!formValidation?.success || loading}
              >
                {loading ?
                  languageValues.modalExperiencia.buttons.carregando :
                  languageValues.modalExperiencia.buttons.finalizar}
              </Button>
            </div>
          </Box>
        </Container>
      </Box>
    </Dialog>
  );
};

export default ModalExperienciaFormulario;
